import React, { createContext, useCallback, useContext, useMemo } from 'react'
import { EvidenceToControlMappingInfo } from '@trustero/trustero-api-web/lib/evidencemgmt/evidencemgmt_pb'
import { EvidenceManagementPromiseClient } from '@trustero/trustero-api-web/lib/evidencemgmt/evidencemgmt_grpc_web_pb'
import { useGrpcRevalidateByMethod } from 'src/components'
import { usePollEvidenceControlMappings } from '../evidence.hooks'

export type EvidenceControlMappingsPollContextValue = {
  mappings: EvidenceToControlMappingInfo[]
  isPolling: boolean
  isLoading: boolean
  isValidating: boolean
  revalidateMappings: () => Promise<void>
}

const EvidenceControlMappingsPollContext =
  createContext<EvidenceControlMappingsPollContextValue | null>(null)

export const EvidenceControlMappingsPollProvider = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => {
  const { mappings, isPolling, isLoading, isValidating } =
    usePollEvidenceControlMappings({
      defaultToAll: true,
    })
  const revalidateByMethod = useGrpcRevalidateByMethod()
  const revalidateMappings = useCallback(async () => {
    await revalidateByMethod(
      EvidenceManagementPromiseClient.prototype.getEvidenceToControlMappingInfo,
    )
  }, [revalidateByMethod])

  const value = useMemo(
    () => ({
      mappings,
      isPolling,
      isLoading,
      isValidating,
      revalidateMappings,
    }),
    [mappings, isPolling, isLoading, isValidating, revalidateMappings],
  )

  return (
    <EvidenceControlMappingsPollContext.Provider value={value}>
      {children}
    </EvidenceControlMappingsPollContext.Provider>
  )
}

export const useEvidenceControlMappingsPollContext =
  (): EvidenceControlMappingsPollContextValue => {
    const ctx = useContext(EvidenceControlMappingsPollContext)
    if (!ctx) {
      throw new Error(
        'useEvidenceControlMappingsPollContext must be used within EvidenceControlMappingsPollProvider',
      )
    }
    return ctx
  }
